// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tile,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import React, { useState } from "react"
import {
  formatAppointmentDate,
  formatAppointmentDateLong,
  formatAppointmentTime,
  formatProviderName,
  getDaysUntil
} from "./formatting"
import { AppointmentsAvatar } from "./AppointmentsAvatar"
import type { UpcomingAppointment } from "./types"

// Placeholder data for scaffolding only. Will be replaced with data from the API.
const daysFromNow = (days: number): Date => {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date
}

const upcomingAppointment: UpcomingAppointment = {
  provider: { name: "Marcus Reed", title: "MD", photoUrl: null },
  date: daysFromNow(3)
}

function CancelAppointmentDialog({
  open,
  canceled,
  onConfirmCancel,
  onClose,
  providerName,
  whenLabel
}: {
  open: boolean
  canceled: boolean
  onConfirmCancel: () => void
  onClose: () => void
  providerName: string
  whenLabel: string
}): JSX.Element {
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="cancel-appointment-title">
      <DialogTitle id="cancel-appointment-title">
        {canceled ? "Appointment canceled" : "Cancel this appointment?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {canceled
            ? `Your visit with ${providerName} on ${whenLabel} has been canceled. You can book a new visit whenever you're ready.`
            : `Your visit with ${providerName} on ${whenLabel} will be canceled. You can rebook anytime, though this slot may not stay open.`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {canceled ? (
          <Button variant="contained" color="secondary" onClick={onClose}>
            Okay
          </Button>
        ) : (
          <>
            <Button variant="contained" color="error" onClick={onConfirmCancel}>
              Cancel appointment
            </Button>
            <Button variant="outlined" color="secondary" onClick={onClose}>
              Keep appointment
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

function AppointmentActions({ providerName, whenLabel }: { providerName: string; whenLabel: string }): JSX.Element {
  const theme = useTheme()
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [canceled, setCanceled] = useState(false)

  const openCancelDialog = () => {
    setCanceled(false)
    setCancelDialogOpen(true)
  }

  return (
    <>
      <Stack sx={{ flexDirection: "row", alignItems: "center" }} spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <Button variant="outlined" color="secondary" onClick={openCancelDialog}>
          Cancel
        </Button>
        {/* Add link to pre-visit intake flow here if they haven't completed it yet */}
        <Button variant="contained" color="secondary">
          Prepare for visit
        </Button>
      </Stack>
      <CancelAppointmentDialog
        open={cancelDialogOpen}
        canceled={canceled}
        // TODO: send the cancellation request here once the API is wired up.
        onConfirmCancel={() => setCanceled(true)}
        onClose={() => setCancelDialogOpen(false)}
        providerName={providerName}
        whenLabel={whenLabel}
      />
    </>
  )
}

export function UpNext(): JSX.Element {
  const theme = useTheme()
  const { provider, date } = upcomingAppointment
  const dateLabel = formatAppointmentDate(date)
  const timeLabel = formatAppointmentTime(date)
  const daysUntil = getDaysUntil(date)
  const whenLabel = `${formatAppointmentDateLong(date)} • ${timeLabel}`

  return (
    <>
      <Typography variant="h3">Up next</Typography>
      <Tile variant="solid" title="Up next" sx={{ backgroundColor: theme.palette.background.paper }}>
        <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} sx={{ inlineSize: "100%" }}>
          <Stack
            sx={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start"
            }}
            spacing={theme.spacing(theme.tokens.spacing.lg)}
          >
            <Stack
              sx={{ flexDirection: "row", alignItems: "flex-start" }}
              spacing={theme.spacing(theme.tokens.spacing.lg)}
            >
              <AppointmentsAvatar name={provider.name} photoUrl={provider.photoUrl} />
              <Box sx={{ flexDirection: "column", justifyContent: "center", alignSelf: "center" }}>
                <Typography variant="h6">{formatProviderName(provider)}</Typography>
                <Typography>{`${dateLabel} • ${timeLabel}`}</Typography>
              </Box>
            </Stack>
            <Chip label={`${dateLabel} • in ${daysUntil} days`} />
          </Stack>
          <AppointmentActions providerName={formatProviderName(provider)} whenLabel={whenLabel} />
        </Stack>
      </Tile>
    </>
  )
}
